import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchLearnerState,
  getStoredAccess,
  redeemAcademyCode,
  setStoredAccess,
  courseIsUnlocked,
  type AcademyAccess,
  type AcademyLearnerState,
} from '@/lib/academyAccess';

/**
 * Academy learner session for the current browser: the redeemed access token
 * and everything it unlocks. Persisted across visits (token in localStorage,
 * validated against the server on load) so returning learners resume without a
 * login. This is the only learner identity the Academy uses — never SSO.
 */
interface AcademyAccessContextValue {
  access: AcademyAccess | null;
  learner: AcademyLearnerState | null;
  loading: boolean;
  /** Redeem a code + email; stores the token on success. */
  signIn: (code: string, email: string) => Promise<{ ok: boolean; error?: string }>;
  /** Forget the token in this browser. */
  signOut: () => void;
  /** Re-check the stored token against the server. */
  refresh: () => void;
  /** Whether a course slug is unlocked for the current learner. */
  isCourseUnlocked: (slug: string) => boolean;
}

const AcademyAccessContext = createContext<AcademyAccessContextValue | undefined>(undefined);

export function AcademyAccessProvider({ children }: { children: ReactNode }) {
  const [access, setAccess] = useState<AcademyAccess | null>(() => getStoredAccess());
  const [learner, setLearner] = useState<AcademyLearnerState | null>(null);
  const [loading, setLoading] = useState<boolean>(() => Boolean(getStoredAccess()));
  const [nonce, setNonce] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // Validate the stored token whenever it changes (or on an explicit refresh).
  useEffect(() => {
    const current = getStoredAccess();
    if (!current?.token) {
      setAccess(null);
      setLearner(null);
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const state = await fetchLearnerState(current.token);
        if (!alive) return;
        if (state) {
          setAccess(current);
          setLearner(state);
        } else {
          // Token no longer valid (revoked / expired) — clear it.
          setStoredAccess(null);
          setAccess(null);
          setLearner(null);
        }
      } catch {
        if (alive) setLearner(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [nonce, access?.token]);

  const signIn = useCallback(async (code: string, email: string) => {
    const result = await redeemAcademyCode(code, email);
    if (!result.ok || !result.access) {
      return { ok: false, error: result.error || 'That code could not be redeemed.' };
    }
    setAccess(result.access);
    try {
      const state = await fetchLearnerState(result.access.token);
      if (mounted.current) setLearner(state);
    } catch {
      // The code was redeemed and stored; only the extra validation call failed.
      // Keep the learner signed in rather than bouncing them back to redeem.
    }
    return { ok: true };
  }, []);

  const signOut = useCallback(() => {
    setStoredAccess(null);
    setAccess(null);
    setLearner(null);
  }, []);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  const isCourseUnlocked = useCallback(
    (slug: string) => courseIsUnlocked(learner, slug),
    [learner],
  );

  const value = useMemo<AcademyAccessContextValue>(
    () => ({ access, learner, loading, signIn, signOut, refresh, isCourseUnlocked }),
    [access, learner, loading, signIn, signOut, refresh, isCourseUnlocked],
  );

  return (
    <AcademyAccessContext.Provider value={value}>
      {children}
    </AcademyAccessContext.Provider>
  );
}

export function useAcademyAccess(): AcademyAccessContextValue {
  const ctx = useContext(AcademyAccessContext);
  if (!ctx) throw new Error('useAcademyAccess must be used within an AcademyAccessProvider');
  return ctx;
}